import React, { useEffect, useState } from 'react'
import { Typography, Link } from '@grammarly/badger-ui'
import { IconButton, InterfaceInfoIcon, Popover, Text } from '@grammarly/design-system'

import { withPublicAssetUrl } from '../../../lib/utils/cdn'
import { useBrowserService } from '../../../lib/hooks'
import { faqSectionIdPrefix } from './utils'

import styles from './ai-detector-shared.module.scss'

const { HS, TL } = Typography

export const OutputDetectedShape = () => (
  <img
    src={`${withPublicAssetUrl('ai-detector/output-detected-shape.svg')}`}
    alt=""
    className={styles.outputDetectedShape}
  />
)

export const OutputPopoverExplanation = () => {
  const { isMobileOrTablet } = useBrowserService()
  const [popoverIsOpen, setPopoverIsOpen] = useState(false)
  const [sectionFaqId, setSectionFaqId] = useState<string | undefined>(undefined)

  useEffect(() => {
    const maybeSectionFaqId = document.querySelector(`[id^=${faqSectionIdPrefix}]`)?.id

    if (maybeSectionFaqId && maybeSectionFaqId !== sectionFaqId) {
      setSectionFaqId(maybeSectionFaqId)
    }
  }, [sectionFaqId, setSectionFaqId])

  return (
    <Popover placement="top" dismissOnOutsideClick open={popoverIsOpen} onOpenChange={setPopoverIsOpen}>
      <Popover.Anchor>
        <IconButton
          icon={InterfaceInfoIcon}
          accessibilityLabel="How it works"
          onClick={() => setPopoverIsOpen(!popoverIsOpen)}
          variant="tertiary"
          tooltipProps={{ open: false }}
        />
      </Popover.Anchor>
      <Popover.Content
        accessibilityLabel="This tool detects AI content by analyzing patterns in the text provided against patterns common to text generated or modified with AI."
        title="How are results determined?"
        width={isMobileOrTablet ? 300 : 400}
      >
        <Text variant="text-small" as="p" className={styles.popoverContent}>
          Grammarly&apos;s AI detector analyzes your text against a large body of AI-generated content to spot patterns
          and characteristics typical of text generated with AI.
          <br />
          <br />
          To learn more about AI detection,{' '}
          <Link href={`#${sectionFaqId}`} className={styles.faqLink}>
            read our FAQs
          </Link>
          .
        </Text>
      </Popover.Content>
    </Popover>
  )
}

export const OutputTitle: React.FC<{ aiPercent: number | null }> = ({ aiPercent }) => (
  <div>
    <HS as={'h2'} className={styles.outputTitle} data-component-name="output-title">
      <b>{aiPercent}%</b> of this text appears to be AI-generated
    </HS>
    <OutputPopoverExplanation />
  </div>
)

export const OutputAiDetected: React.FC<{ aiPercent: number | null }> = ({ aiPercent }) => {
  const hasAiPercent = aiPercent !== null
  return (
    <div className={styles.outputContent}>
      <TL as="div" className={styles.outputNextSteps}>
        {hasAiPercent && aiPercent > 0 ? 'Pinpoint where AI might’ve been used in this text' : 'Go beyond AI detection'}
      </TL>
    </div>
  )
}
